import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import store from '@/store';
import { Question } from './question.model';
import { PaginationModel } from '@/utils/pagination';
import { ErrorResponse } from '@/utils/response';
import { axios, CustomAxiosResponse } from '@/utils/axios';

export interface FetchQuestionsParams {
  subjectUuid: string;
  chapterIds: string[];
  languages: string[];
  difficulties: string[];
  keyword: string;
  sortBy: string;
  level?: number;
}
export interface QuestionStoreInterface {
  questions: Question[];
  questionCount: number | null;
  isPublic: boolean;
  isSubjective: boolean;
  error: ErrorResponse | null;
  pagination: PaginationModel;
  fetchQuestions(params: FetchQuestionsParams): Promise<void>;
  fetchQuestionsCount(params: FetchQuestionsParams): Promise<void>;
  changeIsPublic(params: boolean): Promise<void>;
  changeIsSubjective(params: boolean): Promise<void>;
  resetQuestions(): void;
}

@Module({
  namespaced: true,
  dynamic: true,
  name: 'questions',
  store,
})
class QuestionStore extends VuexModule {
  questions: Question[] = [];
  questionCount: number | null = null;
  isPublic = true;
  error: ErrorResponse | null = null;
  pagination = { ...this.defaultPagination };
  isSubjective = false;

  get defaultPagination(): PaginationModel {
    return {
      page: 1,
      limit: 3,
      totalPages: 1,
      totalRecords: 1,
      isResetPage: false,
    };
  }

  @Mutation
  updateQuestions(params: Question[]) {
    if (this.questions.length === 0 || this.pagination.isResetPage) {
      this.questions = params;
      this.pagination.isResetPage = false;
    } else {
      this.questions.push(...params);
    }
  }

  @Mutation
  updateQuestionCount(params: number) {
    this.questionCount = params;
  }

  @Mutation
  updatePagination(params: PaginationModel) {
    this.pagination = params;
  }

  @Mutation
  updateIsPublic(params: boolean) {
    this.isPublic = params;
  }

  @Mutation
  updateIsSubjective(params: boolean) {
    this.isSubjective = params;
  }

  @Mutation
  setError(params: ErrorResponse) {
    this.error = params;
  }

  @Action
  async changeIsPublic(params: boolean): Promise<void> {
    this.context.commit('updateIsPublic', params);
  }

  @Action
  async changeIsSubjective(params: boolean): Promise<void> {
    this.context.commit('updateIsSubjective', params);
  }

  @Action
  async fetchQuestions(params: FetchQuestionsParams): Promise<void> {
    const {
      subjectUuid,
      chapterIds,
      languages,
      difficulties,
      keyword,
      sortBy,
      level,
    } = params;

    this.context.commit('updateQuestions', []);
    this.context.commit('setError', null);

    const publicUrl = this.isPublic ? '' : '';

    try {
      const { data, pagination }: CustomAxiosResponse = await axios.get(
        `questions${publicUrl}`,
        {
          params: {
            subject_uuid: subjectUuid,
            chapters: `${chapterIds}`,
            languages,
            difficulties: `${difficulties}`,
            keyword,
            sort_by: sortBy,
            page: this.pagination.page,
            limit: this.pagination.limit,
            is_subjective: this.isSubjective,
            form: level || '',
          },
        },
      );

      this.context.commit('updateQuestions', data);
      this.context.commit('updatePagination', pagination);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async fetchQuestionsCount(params: FetchQuestionsParams): Promise<void> {
    const {
      subjectUuid,
      chapterIds,
      languages,
      difficulties,
      keyword,
      sortBy,
      level,
    } = params;

    this.context.commit('updateQuestionCount', null);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.get(`questions/count`, {
        params: {
          subject_uuid: subjectUuid,
          chapters: `${chapterIds}`,
          languages,
          difficulties: `${difficulties}`,
          keyword,
          sort_by: sortBy,
          page: this.pagination.page,
          limit: this.pagination.limit,
          is_subjective: this.isSubjective,
          form: level || '',
        },
      });

      this.context.commit('updateQuestionCount', data.count);
    } catch (error) {
      this.context.commit('updateQuestionCount', null);
      this.context.commit('setError', error);
    }
  }

  @Action
  async resetQuestions(): Promise<void> {
    this.context.commit('updateQuestions', []);
  }
}

export default getModule(QuestionStore);
